import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { MessageSquare, RotateCcw } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { ChatInput } from '@/components/chat/ChatInput';
import { ChatMessages } from '@/components/chat/ChatMessages';
import { Breadcrumb, BreadcrumbPortal, HeaderActionsPortal } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { getToken, logout, refreshAccessToken } from '@/services/auth.session';
import { useStore } from '@/store/store';
import { TIMEZONE_MAP } from '@/utils/date';

export function ChatContainer() {
  const [input, setInput] = useState('');
  const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>([]);
  const selectedOrganizationId = useStore(s => s.selectedOrganizationId);
  const timeZone = useStore(s => s.timeZone);

  const transport = useMemo(() => {
    // Build auth headers fresh on every request so an expired/rotated token
    // is never sent. Mirrors the axios apiClient request interceptor.
    const applyAuthHeaders = (base?: HeadersInit): Headers => {
      const headers = new Headers(base);
      const token = getToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      if (selectedOrganizationId) headers.set('X-Organization-Id', selectedOrganizationId);
      // The assistant resolves "today"/"yesterday" against this. Send the timezone the
      // user picked in the header selector, not the browser's — it's what every other
      // timestamp in the dashboard is rendered in.
      headers.set('X-Timezone', TIMEZONE_MAP[timeZone]);
      return headers;
    };

    // The AI SDK transport uses its own fetch and bypasses the axios apiClient
    // interceptor, so it gets none of the 401 refresh-and-retry handling.
    // Replicate that here: on a 401, refresh the token once and retry; if the
    // refresh fails, log out and redirect to login (matches handleRefreshFailure).
    const fetchWithAuthRetry: typeof fetch = async (input, init) => {
      let response = await fetch(input, { ...init, headers: applyAuthHeaders(init?.headers) });

      if (response.status === 401) {
        const refreshed = await refreshAccessToken();
        if (refreshed?.access_token) {
          response = await fetch(input, { ...init, headers: applyAuthHeaders(init?.headers) });
        } else {
          await logout();
          toast.error('Session Expired: Please login again.');
          const currentPath =
            window.location.pathname + window.location.search + window.location.hash;
          window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
        }
      }

      return response;
    };

    return new DefaultChatTransport({
      api: '/ai-chat/api/chat',
      fetch: fetchWithAuthRetry,
    });
  }, [selectedOrganizationId, timeZone]);

  const { messages, sendMessage, status, stop, setMessages } = useChat({
    transport,
    onFinish: ({ message }) => {
      // Extract suggested follow-ups from the last tool invocation
      for (const part of message.parts) {
        if (
          part.type === 'dynamic-tool' &&
          part.toolName === 'suggestFollowUps' &&
          part.state === 'output-available'
        ) {
          const result = part.output as { suggestions?: string[] };
          if (result?.suggestions) {
            setSuggestedPrompts(result.suggestions);
          }
        }
      }
    },
  });

  const isLoading = status === 'submitted' || status === 'streaming';

  const handleSubmit = useCallback(() => {
    if (!input.trim()) return;
    const text = input;
    setInput('');
    setSuggestedPrompts([]);
    sendMessage({ text });
  }, [input, sendMessage]);

  const handleNewChat = useCallback(() => {
    setMessages([]);
    setInput('');
    setSuggestedPrompts([]);
  }, [setMessages]);

  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item>AI Chat</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  const headerActions = (
    <HeaderActionsPortal>
      <Button size="sm" variant="outline" onClick={handleNewChat}>
        <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
        New Chat
      </Button>
    </HeaderActionsPortal>
  );

  return (
    <>
      {breadcrumb}
      {headerActions}
      <div className="flex h-[calc(100vh-64px)] flex-col">
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-medium">Case Management Assistant</h2>
        </div>
        <ChatMessages messages={messages} isLoading={isLoading} />
        <ChatInput
          input={input}
          onInputChange={setInput}
          onSubmit={handleSubmit}
          onStop={stop}
          isLoading={isLoading}
          suggestedPrompts={suggestedPrompts}
        />
      </div>
    </>
  );
}
